import { AlertTriangle } from 'lucide-react'
import { toast } from 'sonner'

import { useLinkChannel, useLinkPreview } from '@/api/endpoints/pools'
import { Button } from '@/components/ui/Button'
import { DialogContent, DialogRoot } from '@/components/ui/Dialog'
import { Spinner } from '@/components/ui/Spinner'
import { PreviewLines } from '@/features/pools/components/PreviewLines'
import { linkPreviewLines } from '@/features/pools/previewText'
import { errorMessage } from '@/lib/errors'
import styles from './LinkPoolDialog.module.scss'
import { QueryErrorNotice } from '@/components/feedback/QueryErrorNotice'

interface LinkPoolDialogProps {
  open: boolean
  onClose: () => void
  poolSlug: string
  poolName: string
  channelSlug: string
  channelName: string
  onLinked?: () => void
}

/**
 * "Usar o pool X?": os números da prévia (o que vai para o pool, o que fica
 * como reserva, o que é removido) antes de ligar o canal. A ligação age na
 * hora, fora do "Salvar" do diálogo do canal, porque move vídeos.
 */
export function LinkPoolDialog({
  open,
  onClose,
  poolSlug,
  poolName,
  channelSlug,
  channelName,
  onLinked,
}: LinkPoolDialogProps) {
  const preview = useLinkPreview(open ? poolSlug : null, open ? channelSlug : null)
  const link = useLinkChannel()
  const text = preview.data ? linkPreviewLines(preview.data) : null

  const confirm = () => {
    link.mutate(
      { slug: poolSlug, channel: channelSlug },
      {
        onSuccess: () => {
          toast.success(`${channelName} agora publica do pool ${poolName}`)
          onLinked?.()
          onClose()
        },
        onError: (err) => {
          toast.error(errorMessage(err, 'Não foi possível ligar o canal ao pool'))
        },
      },
    )
  }

  return (
    <DialogRoot
      open={open}
      onOpenChange={(next) => {
        if (!next) onClose()
      }}
    >
      <DialogContent
        role="alertdialog"
        className={styles.dialog}
        title={`Usar o pool ${poolName}?`}
        description={`${channelName} deixa de gerar e passa a publicar os vídeos livres do pool. Cada vídeo que ele pegar passa a ser só dele.`}
        footer={
          <>
            <Button variant="secondary" onClick={onClose}>
              Cancelar
            </Button>
            <Button
              variant={text && text.lines.some((l) => l.tone === 'danger') ? 'danger' : 'primary'}
              disabled={!text || text.blocked || link.isPending}
              leadingIcon={link.isPending ? <Spinner /> : undefined}
              onClick={confirm}
            >
              {text?.confirmLabel ?? 'Usar o pool'}
            </Button>
          </>
        }
      >
        {preview.isPending ? (
          <p className={styles.muted}>Calculando o que acontece com os vídeos do canal…</p>
        ) : preview.isError ? (
          <QueryErrorNotice
            compact
            error={preview.error}
            label="a prévia"
            onRetry={() => {
              void preview.refetch()
            }}
          />
        ) : text ? (
          <>
            <PreviewLines lines={text.lines} />
            {text.blocked ? (
              <p className={styles.error}>
                <AlertTriangle aria-hidden />
                Há vídeo deste canal com upload em andamento. Espere terminar e tente de novo.
              </p>
            ) : null}
          </>
        ) : null}
      </DialogContent>
    </DialogRoot>
  )
}
